import React from 'react';
import {
  SectionHeading,
  StyledAsterisksText,
  SubSection,
  InputRow,
  FormSection,
  largeInputBorderStyle,
} from '@/shared/styles/styledComponents/editProfileForm';
import { green } from '@/shared/styles/colors';
import { BioData } from '@/shared/types/stylist';
import useBreakpoint from '@/shared/hooks/useBreakpoint';
import ErrorFrame from '@/shared/components/ErrorFrame';
import { BIO_MAX_LENGTH, HANDLE_MAX_LENGTH } from '@/shared/constants/utility';
import StaticInput from '@/shared/components/StaticInput';
import useStore from '@/stores/useStore';

interface About {
  bioData: BioData;
  setBioData: React.Dispatch<React.SetStateAction<BioData>>;
}

const AboutYouSection: React.FC<About> = ({ bioData, setBioData }) => {
  const { isDesktop } = useBreakpoint();
  const isStylist = useStore((state) => state.isStylist);

  return (
    <FormSection isDesktop={isDesktop} marginBottom={36}>
      {isStylist && (
        <SectionHeading isDesktop={isDesktop}>About You</SectionHeading>
      )}
      <SubSection>
        <StyledAsterisksText marginBottom={24}>
          {isStylist ? 'Short Bio*' : 'Bio (optional)'}
        </StyledAsterisksText>
        <ErrorFrame
          errorText={`Cannot exceed ${BIO_MAX_LENGTH} characters`}
          errorVisible={bioData.bio.length > BIO_MAX_LENGTH}
          containerStyle={{ marginBottom: 6 }}
        >
          <StaticInput
            placeholderText={
              isStylist
                ? 'Tell us about yourself'
                : 'Share a brief bio about yourself for\nstylists to get to know you better.'
            }
            value={bioData.bio}
            onChange={(e) =>
              setBioData({ ...bioData, bio: e.nativeEvent.text })
            }
            labelText="BIO"
            multiline
            outlineStyle={largeInputBorderStyle}
            contentStyle={{
              paddingHorizontal: 18,
              borderRadius: 8,
              paddingBottom: 16,
              paddingTop: 16,
            }}
            customStyles={{
              height: isDesktop ? 90 : 144,
              minHeight: isDesktop ? 90 : 144,
              justifyContent: 'flex-start',
              borderRadius: 8,
              color: green,
              marginBottom: 0,
            }}
          />
        </ErrorFrame>
      </SubSection>
      {isStylist && (
        <>
          <StyledAsterisksText marginBottom={16}>
            Social Media Platform (Optional)
          </StyledAsterisksText>
          <SubSection marginBottom={0}>
            <InputRow isDesktop={isDesktop}>
              <ErrorFrame
                errorText={`Cannot exceed ${HANDLE_MAX_LENGTH} characters`}
                errorVisible={bioData.instagram.length > HANDLE_MAX_LENGTH}
                containerStyle={{ flex: 1 }}
              >
                <StaticInput
                  placeholderText="Enter instagram handle here"
                  value={bioData.instagram}
                  onChange={(e) =>
                    setBioData({ ...bioData, instagram: e.nativeEvent.text })
                  }
                  labelText="INSTAGRAM"
                />
              </ErrorFrame>
              <ErrorFrame
                errorText={`Cannot exceed ${HANDLE_MAX_LENGTH} characters`}
                errorVisible={bioData.tikTok.length > HANDLE_MAX_LENGTH}
                containerStyle={{ flex: 1 }}
              >
                <StaticInput
                  placeholderText="Enter tiktok handle here"
                  value={bioData.tikTok}
                  onChange={(e) =>
                    setBioData({ ...bioData, tikTok: e.nativeEvent.text })
                  }
                  labelText="TIKTOK"
                />
              </ErrorFrame>
              <ErrorFrame
                errorText={`Cannot exceed ${HANDLE_MAX_LENGTH} characters`}
                errorVisible={bioData.substack.length > HANDLE_MAX_LENGTH}
                containerStyle={{ flex: 1 }}
              >
                <StaticInput
                  placeholderText="Enter substack handle here"
                  value={bioData.substack}
                  onChange={(e) =>
                    setBioData({ ...bioData, substack: e.nativeEvent.text })
                  }
                  labelText="SUBSTACK"
                />
              </ErrorFrame>
            </InputRow>
          </SubSection>
        </>
      )}
    </FormSection>
  );
};

export default AboutYouSection;
